'use client';

import { fetchMemberPrivileges } from '@/api/account';
import Dialog from '@/components/Dialog';
import MobileNav from '@/components/MobileNav';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Drawer } from '@mui/material';
import { WapNavBackOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useEffect, useState } from 'react';

interface MembershipDescriptionProps {
  userInfo: {
    is_member: number;
  };
  onClose?: () => void;
  openDrawer?: boolean;
  closeDrawer?: () => void;
}

export default function MembershipDescription({
  userInfo,
  onClose,
  openDrawer = false,
  closeDrawer = () => {},
}: MembershipDescriptionProps) {
  const t = useTranslations('zeeloolRewards');
  const [levelHtml, setLevelHtml] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { deviceType } = useContext(ResponsiveContext);
  const exitMember = useStore((state) => state.exitMember);

  const fetchData = async () => {
    try {
      const { code, data } = await fetchMemberPrivileges();
      if (code === 200) {
        setLevelHtml(data.member_rules_explain);
      }
    } catch (error) {
      logger.error('Failed to fetch member privileges:', error);
    }
  };

  const setShowExitRewards = () => {
    onClose?.();

    exitMember();

    if (deviceType !== EPlatform.WAP) {
      (window as any).CloseWebView.postMessage('something');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const content = (
    <>
      <div
        className={`text-14 text-primary-black font-br`}
        dangerouslySetInnerHTML={{ __html: levelHtml }}
      />
      {userInfo?.is_member === 1 && (
        <div
          className="text-12 sm:text-16 text-primary-black cursor-pointer underline mt-[60px] text-center"
          onClick={() => {
            setShowConfirmDialog(true);
          }}
        >
          {t('memberLevel.exitZeeloolRewards')}
        </div>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title={t('memberLevel.exitConfirmTitle')}
        okText={t('common.no')}
        cancelText={t('common.yes')}
        onOk={() => setShowConfirmDialog(false)}
        onCancel={() => {
          setShowExitRewards();
          setShowConfirmDialog(false);
        }}
        PaperProps={{
          sx: {
            background: 'linear-gradient(180deg, #ecf2ff, #fff)',
            width: '400px',
            maxWidth: '90%',
          },
        }}
      >
        <p className="text-center text-14 text-auxiliary-text">{t('memberLevel.exitWarning')}</p>
      </Dialog>
    </>
  );

  return (
    <>
      <WebOnly>
        <div className=" bg-white pb-[60px] flex flex-col w-full">
          <div className="flex items-center p-[32px] relative mt-10">
            <div
              className="cursor-pointer hover:text-primary-purple text-16 text-primary-black"
              onClick={() => onClose()}
            >
              {t('navigation.title')}
            </div>
            <div className="mx-[8px]">/</div>
            <div>{t('memberLevel.membershipDescription')}</div>
            <button
              className="absolute right-8 top-1/2 -translate-y-1/2 text-24 text-primary-black hover:text-primary-purple font-bold cursor-pointer border-none bg-transparent outline-none"
              onClick={() => onClose()}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
          <div className="flex-1 overflow-auto px-[32px] pb-[100px]">{content}</div>
        </div>
      </WebOnly>
      <WapOnly>
        <Drawer
          open={openDrawer}
          PaperProps={{
            sx: {
              width: '100%',
              background: '#ffffff',
            },
          }}
          sx={{
            zIndex: 250,
          }}
        >
          <div className="h-full w-full relative font-br text-primary-black">
            <MobileNav
              title={t('memberLevel.membershipDescription')}
              titleClassName="text-center w-full"
              className="bg-primary-white"
              leftSlot={
                <div
                  className="MobileNav flex items-center justify-center h-[40px] w-[40px] absolute left-0 top-5"
                  onClick={() => closeDrawer()}
                >
                  <WapNavBackOutlined className="text-20" />
                </div>
              }
            />
            <div className="flex-1 flex-shrink-0 pb-60">
              <div className="bg-primary-white box-border ml-16 mr-16 h-full  rounded-bl-[2.24vw] rounded-br-[2.24vw] pt-[50px]">
                {content}
              </div>
            </div>
          </div>
        </Drawer>
      </WapOnly>
    </>
  );
}
